import { DomainSheet, DomainView } from "@/components/domain";
import { PropertyList } from "@/components/property-list";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { useSheetContext } from "@/context/sheet";
import { useGetClient } from "@/hooks/clients";
import { useGetGuide } from "@/hooks/guides";
import { useGetHotelRoom } from "@/hooks/hotel-rooms";
import { useGetReservationClients } from "@/hooks/reservation-clients";
import { useDeleteReservation, useGetReservation } from "@/hooks/reservations";
import { useGetService } from "@/hooks/services";
import { useGetTourAgency } from "@/hooks/tour-agencies";
import { useGetWorker } from "@/hooks/workers";
import { T } from "@/i18n";
import {
  dateFormatedToString,
  dateToString,
  formatPhoneNumber,
} from "@/lib/utils";
import {
  Accommodation,
  ReservationClient,
  ReservationDay,
  ReservationService,
  RouterPararms,
  RouterPaths,
} from "@/models";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import moment from "moment";
import { Key, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { clientKeysToList } from "../-components/reservation-form/clients";
import { useGetFullReservation } from "../-hooks/use-get-full-reservation";
import { ScheduleManager } from "../-managers";
import { generateReservationLabel } from "../-utils";
import { ReservationsPage } from "../reservations.index";

export const Route = createFileRoute("/(reservations)/reservations/$id/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  return (
    <>
      <ReservationsPage />
      <DomainSheet
        noOutlet
        onClose={() => {
          navigate({ to: "/reservations" });
        }}
      >
        <ReservationDetail
          id={id}
          onDeleteSuccess={() => {
            navigate({ to: "/reservations" });
          }}
          editLinkProps={{
            to: "/reservations/$id/edit",
            params: { id },
          }}
        />
      </DomainSheet>
    </>
  );
}

export function ReservationDetail<T extends RouterPaths>({
  id,
  onDeleteSuccess,
  editLinkProps,
}: {
  id: string;
  onDeleteSuccess: () => void;
  editLinkProps: {
    to: T;
    params: RouterPararms<T>;
  };
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetReservation(id);
  const { data: tourAgency, isLoading: tourAgencyLoading } = useGetTourAgency(
    "agency",
    data?.tourAgencyId?.toString() ?? "",
    {
      enabled: typeof data?.tourAgencyId === "number",
    },
  );
  const { data: groupLeader, isLoading: groupLeaderLoading } = useGetClient(
    data?.groupLeaderId?.toString() ?? "",
    {
      enabled: typeof data?.groupLeaderId === "number",
    },
  );
  const { data: guide, isLoading: guideLoading } = useGetGuide(
    data?.tourLeaderId?.toString() ?? "",
    { enabled: typeof data?.tourLeaderId === "number" },
  );
  const { data: leader, isLoading: leaderLoading } = useGetClient(
    data?.tourLeaderId?.toString() ?? "",
    { enabled: typeof data?.tourLeaderId === "number" },
  );
  const { data: worker, isLoading: workerLoading } = useGetWorker(
    data?.workerId?.toString() ?? "",
    {
      enabled: typeof data?.workerId === "number",
    },
  );
  const { close } = useSheetContext();
  const { mutate: deleteReservation } = useDeleteReservation({
    onSuccess: () => {
      close(onDeleteSuccess);
    },
  });

  return (
    <DomainView
      domain="reservations"
      editLink={(children, key) => (
        <Link
          to={"/reservations/$id/edit"}
          params={editLinkProps.params as never}
          key={key as Key}
        >
          {children}
        </Link>
      )}
      isLoading={isLoading || tourAgencyLoading || groupLeaderLoading}
      title={
        !data
          ? undefined
          : generateReservationLabel(data, tourAgency, groupLeader)
      }
      onDelete={() => deleteReservation(id)}
      propsList={[
        {
          title: "reservations.details.agency",
          value:
            tourAgencyLoading || !tourAgency ? (
              <Skeleton className="h-2 w-full" />
            ) : (
              (tourAgency.agency?.name ?? "-")
            ),
        },
        {
          title: "reservations.details.tour",
          value:
            tourAgencyLoading || !tourAgency ? (
              <Skeleton className="h-2 w-full" />
            ) : (
              (tourAgency.tour?.name ?? "-")
            ),
        },
        {
          title: "reservations.fields.tourLeaderId",
          value:
            guideLoading || !guide ? (
              <Skeleton className="h-2 w-full" />
            ) : (
              (guide.name ?? "-")
            ),
        },
        {
          title: "reservations.fields.groupLeaderId",
          value:
            leaderLoading || !leader ? (
              <Skeleton className="h-2 w-full" />
            ) : (
              (leader.name ?? "-")
            ),
        },
        {
          title: "reservations.fields.operationRegion",
          value: data?.operationRegion
            ? t(`common.operationRegions.${data.operationRegion}`)
            : "-",
        },
        {
          title: "reservations.details.dates",
          value: `${dateToString(data?.begin)} - ${dateToString(data?.end)}`,
        },
        {
          title: "reservations.fields.documentationSentDate",
          value: dateToString(data?.documentationSentDate),
        },
        {
          title: "reservations.fields.bookingDate",
          value: dateToString(data?.bookingDate),
        },
        {
          title: "reservations.fields.confirmedDate",
          value: dateToString(data?.confirmedDate),
        },
        {
          title: "reservations.fields.agencyReservationNumber",
          value: data?.agencyReservationNumber ?? "-",
        },
        {
          title: "reservations.fields.workerId",
          value:
            workerLoading || !worker ? (
              <Skeleton className="h-2 w-full" />
            ) : (
              (worker.name ?? "-")
            ),
        },
        {
          title: "reservations.fields.state",
          value: data?.operationRegion
            ? t(`reservations.state.${data.state}`)
            : "-",
        },
        {
          title: "reservations.fields.tourStandart",
          value: data?.tourStandart
            ? t(`reservations.tourStandard.${data.tourStandart}`)
            : "-",
        },
        {
          title: "reservations.fields.rwgpsrequired",
          value: !data?.rwgpsrequired ? (
            t(`common.boolean.false`)
          ) : (
            <div className="grid gap-1">
              {data.rwgpslinks?.map((link, i) => (
                <a key={i} href={link} target="_blank">
                  Link {i + 1}
                </a>
              ))}
            </div>
          ),
        },
        {
          title: "reservations.fields.travefyRequired",
          value: !data?.travefyRequired ? (
            t(`common.boolean.false`)
          ) : (
            <div className="grid gap-1">
              {data.tfylinks?.map((link, i) => (
                <a key={i} href={link} target="_blank">
                  Link {i + 1}
                </a>
              ))}
            </div>
          ),
        },
        {
          title: "reservations.fields.gpxRequired",
          value: !data?.gpxRequired ? (
            t(`common.boolean.false`)
          ) : (
            <div className="grid gap-1">
              {data.gpxlinks?.map((link, i) => (
                <a key={i} href={link} target="_blank">
                  Link {i + 1}
                </a>
              ))}
            </div>
          ),
        },
        {
          title: "reservations.fields.dropboxRequired",
          value: !data?.dropboxRequired ? (
            t(`common.boolean.false`)
          ) : (
            <div className="grid gap-1">
              {data.dropboxLinks?.map((link, i) => (
                <a key={i} href={link} target="_blank">
                  Link {i + 1}
                </a>
              ))}
            </div>
          ),
        },
        {
          title: "reservations.fields.feedbackRequired",
          value: !data?.feedbackRequired ? (
            t(`common.boolean.false`)
          ) : (
            <div className="grid gap-1">
              {data.feedbackLinks?.map((link, i) => (
                <a key={i} href={link} target="_blank">
                  Link {i + 1}
                </a>
              ))}
            </div>
          ),
        },
        {
          title: "reservations.fields.customLinks",
          value: (
            <div className="grid gap-1">
              {data?.customLinks?.map((link, i) => (
                <a key={i} href={link} target="_blank">
                  Link {i + 1}
                </a>
              )) ?? "-"}
            </div>
          ),
        },
        {
          title: "reservations.fields.travefySent",
          value: data?.travefySent
            ? t(`reservations.tags.${data.travefySent}`)
            : "-",
        },
        {
          title: "reservations.fields.rwgpsSent",
          value: data?.rwgpsSent
            ? t(`reservations.tags.${data.rwgpsSent}`)
            : "-",
        },
        {
          title: "reservations.fields.gpxSent",
          value: data?.gpxSent ? t(`reservations.tags.${data.gpxSent}`) : "-",
        },
        {
          title: "reservations.fields.details",
          value: data?.details ?? "-",
        },
        {
          title: "reservations.form.tabs.clients",
          value: <ClientsList reservationId={id} />,
        },
        {
          title: "reservations.form.tabs.schedule",
          value: <Schedule reservationId={id} />,
        },
      ]}
    />
  );
}

function ClientsList({ reservationId }: { reservationId: string }) {
  const { data, isLoading } = useGetReservationClients(reservationId);

  if (isLoading)
    return (
      <div className="grid w-full gap-1">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );

  return (
    <Accordion className="w-full" type="multiple">
      {data?.map((c) => <ClientInfo reservationClient={c} key={c.clientId} />)}
    </Accordion>
  );
}

function ClientInfo({
  reservationClient,
}: {
  reservationClient: ReservationClient;
}) {
  const { data, isLoading } = useGetClient(
    reservationClient.clientId.toString(),
  );

  return (
    <AccordionItem value={reservationClient.clientId.toString()}>
      <AccordionTrigger className="w-full">
        {isLoading ? <Skeleton className="h-9 w-full" /> : (data?.name ?? "-")}
      </AccordionTrigger>
      <AccordionContent className="grid gap-1">
        <PropertyList
          item={{ ...(data ?? {}), ...reservationClient }}
          loading={isLoading}
          labelPrefix="reservations.form.clients.fields"
          properties={clientKeysToList}
          propertyMappingOverrides={{
            birthdate: (v) => (!v ? "-" : dateFormatedToString(new Date(v))),
            phone: (v) => (v ? formatPhoneNumber(v) : "-"),
          }}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

function Schedule({ reservationId }: { reservationId: string }) {
  const { data, isLoading } = useGetFullReservation(reservationId);

  if (isLoading || !data)
    return (
      <div className="grid gap-1">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );

  return (
    <ScheduleList
      beginDate={data.reservation?.begin}
      reservationId={reservationId}
      accommodations={data.accommodations ?? []}
      services={data.services ?? []}
      reservationDays={data.days ?? []}
    />
  );
}

export function ScheduleList({
  reservationId,
  accommodations,
  reservationDays,
  services,
  beginDate,
  hideAccommodations,
}: {
  accommodations: Accommodation[];
  services: ReservationService[];
  reservationDays: ReservationDay[];
  reservationId: string;
  beginDate: Date | null | undefined;
  hideAccommodations?: boolean;
}) {
  const [schedule] = useState(
    new ScheduleManager(
      reservationId,
      accommodations,
      services,
      reservationDays,
    ),
  );

  return (
    <Accordion className="w-full" type="multiple">
      {schedule.days.map((day) => {
        const dayNumber = day.reservationDay.value.day;
        const dayDate = moment(beginDate)
          .add(dayNumber - 1, "days")
          .format("ddd YY-MM-DD");
        const name = day.reservationDay.value.name;
        return (
          <AccordionItem
            value={day.reservationDay.value.id}
            key={day.reservationDay.value.id}
          >
            <AccordionTrigger className="w-full">
              Day {dayNumber} - {dayDate} {name ? ` - ${name}` : ""}
            </AccordionTrigger>
            <AccordionContent className="grid gap-3">
              {hideAccommodations ? (
                <></>
              ) : (
                <div className="grid gap-1">
                  <span className="text-base font-medium">
                    <T tKey="reservations.form.schedule.accommodations.title" />
                  </span>
                  {day.accommodations
                    .filter((acc) => acc.operation !== "noop")
                    .map((acc) => acc.value)
                    .map((acc, _, arr) => (
                      <AccommodationDetail
                        accommodation={acc}
                        key={acc.id}
                        showClients={arr.length > 1}
                      />
                    ))}
                </div>
              )}

              <div className="grid gap-1">
                <span className="text-base font-medium">
                  <T tKey="reservations.form.schedule.service.title" />
                </span>
                {day.services
                  .filter((s) => s.operation !== "noop")
                  .map((s) => s.value)
                  .map((s) => (
                    <ServiceDetail service={s} key={s.id} />
                  ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function AccommodationDetail({
  accommodation,
  showClients,
}: {
  accommodation: Accommodation;
  showClients: boolean;
}) {
  return (
    <div className="grid gap-1 text-muted-foreground">
      {typeof accommodation.hotelRoomId === "number" ? (
        <HotelName hotelRoomId={accommodation.hotelRoomId} />
      ) : (
        "-"
      )}

      {showClients ? (
        <div className="grid gap-2 pl-2">
          {accommodation.reservationClients.map((rc) => (
            <ClientName clientId={rc.clientId} key={rc.clientId} />
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

function ClientName({ clientId }: { clientId: number | null | undefined }) {
  const { data, isLoading } = useGetClient(clientId?.toString() ?? "", {
    enabled: typeof clientId === "number",
  });

  if (isLoading || !data) return <Skeleton className="h-3 w-full" />;

  return <>{data.name}</>;
}

function HotelName({
  hotelRoomId,
}: {
  hotelRoomId: number | null | undefined;
}) {
  //TODO: this is a temp fix change after the full implementation of HoteRoomPrices
  const { data, isLoading } = useGetHotelRoom(
    hotelRoomId?.toString() ?? "",
    hotelRoomId?.toString() ?? "",
    {
      enabled: typeof hotelRoomId === "number",
    },
  );

  if (isLoading || !data) return <Skeleton className="h-3 w-full" />;

  return <>{data.hotelName ?? "-"}</>;
}

function ServiceDetail({ service }: { service: ReservationService }) {
  const { data: serviceDetail, isLoading } = useGetService(
    service.serviceId.toString(),
  );
  const hour = useMemo(() => {
    if (service.startHour && service.endHour) {
      return `${service.startHour} - ${service.endHour}`;
    } else if (service.startHour) {
      return (
        <>
          {service.startHour} - <div className="w-[42.1px]" />
        </>
      );
    } else if (service.endHour) {
      return (
        <>
          <div className="w-[42.1px]" /> - {service.endHour}
        </>
      );
    }
    return (
      <>
        <div className="w-[42.1px]" /> - <div className="w-[42.1px]" />
      </>
    );
  }, [service]);
  return (
    <div className="flex items-center gap-2 pl-2">
      <span className="flex items-center font-bold text-primary">{hour}</span>
      {isLoading ? (
        <Skeleton className="h-3 w-full" />
      ) : (
        <span className="text-muted-foreground">{serviceDetail?.name}</span>
      )}
    </div>
  );
}
